import React, { Component, createContext, useContext } from 'react'
import { get } from 'api'
import debounce from 'lodash.debounce'
import isEqual from 'react-fast-compare'
import { reduce } from 'utilities/object'

const ChartContext = createContext()
ChartContext.displayName = 'Chart'

export default class ChartProvider extends Component {
    constructor(props) {
        super(props)

        this.fetchController = new AbortController()
        this.fetchDebounced = debounce(this.fetch, 100, { maxWait: 500, leading: false, trailing: true })

        this.state = {
            chart: props.chart ?? null,
            fixed: !!props.chart,
            filter: pruneFilter(props.filter ?? {}),

            hasFetched: false,
            fetching: false
        }
    }

    componentDidMount() {
        const { fetchOnMount = true } = this.props
        fetchOnMount && this.fetchDebounced()
    }

    componentDidUpdate({ chart }, { filter }) {
        if(this.state.fixed && !chart?.length && !!this.props.chart?.length) {
            this.setState({ chart: this.props.chart })
        }

        const nextFilter = pruneFilter(this.props.filter ?? {})

        if(!isFilterEqual(filter, nextFilter)) {
            this.setFilterFromProps(nextFilter)
        }
    }

    componentWillUnmount() {
        this.fetchController.abort()
    }

    fetch = async (force = false) => {
        const { corporation = false } = this.props

        const {
            fixed,
            fetching,
            filter,
            hasFetched
        } = this.state

        if((fixed || fetching || hasFetched) && !force) {
            return
        }

        if(force || fetching) {
            this.fetchController.abort()
            this.fetchController = new AbortController()
        }

        this.setState({ fetching: true })

        const { ok, response: chart } = await get({
            path: corporation ?
                `/organization/subsidiaries/instruments/${this.props.id}` :
                `/insights/instruments/${this.props.id}`,
            params: filter,
            signal: this.fetchController.signal
        })

        const state = {
            hasFetched: true,
            fetching: false
        }

        if(ok && chart) {
            this.setState({
                chart,
                ...state
            })
        } else {
            this.setState(state)
        }
    }

    setFilterFromProps = (filter = {}) => void this.setState({
        chart: null,
        filter,
        hasFetched: false
    }, () => this.fetchDebounced(true))

    render() {
        const { children = null } = this.props

        return (
            <ChartContext.Provider value={this.state}>
                {(typeof children === 'function') && children(this.state)}
                {(typeof children !== 'function') && children}
            </ChartContext.Provider>
        )
    }
}

export const useChart = () => useContext(ChartContext)

const pruneFilter = filter => reduce(filter, (accumulator, value, key) => ({
    ...accumulator,
    ...((!!value || value === 0) ? { [key] : value } : null)
}))

const isFilterEqual = (current, next) => isEqual(
    pruneFilter(current),
    pruneFilter(next)
)